import { useEffect, useCallback } from 'react';
import { RefreshCw, ExternalLink, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useHelpStore } from '@/lib/help/store';
import { getDocumentation } from '@/lib/help/api';
import { getDocInfoForRoute } from '@/lib/help/routes';
import { MarkdownRenderer } from './MarkdownRenderer';

export function DocumentationTab() {
  const currentRoute = useHelpStore((s) => s.currentRoute);
  const routeHistory = useHelpStore((s) => s.routeHistory);
  const docContent = useHelpStore((s) => s.docContent);
  const docLoading = useHelpStore((s) => s.docLoading);
  const docError = useHelpStore((s) => s.docError);
  const setDocContent = useHelpStore((s) => s.setDocContent);
  const setDocLoading = useHelpStore((s) => s.setDocLoading);
  const setDocError = useHelpStore((s) => s.setDocError);
  const goBack = useHelpStore((s) => s.goBack);

  const canGoBack = routeHistory.length > 0;

  const docInfo = getDocInfoForRoute(currentRoute);

  const loadDocumentation = useCallback(async () => {
    setDocLoading(true);
    setDocError(null);

    try {
      const doc = await getDocumentation(currentRoute);
      if (doc) {
        setDocContent(doc.content);
      } else {
        setDocError('Documentation not found for this page.');
      }
    } catch (error) {
      setDocError('Failed to load documentation.');
      console.error('Documentation load error:', error);
    } finally {
      setDocLoading(false);
    }
  }, [currentRoute, setDocContent, setDocLoading, setDocError]);

  useEffect(() => {
    if (!docContent && !docLoading) {
      loadDocumentation();
    }
  }, [currentRoute, docContent, docLoading, loadDocumentation]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-card">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {canGoBack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={goBack}
              className="flex-shrink-0 h-8 w-8"
              title="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm truncate">{docInfo.title}</h3>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {docInfo.description}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={loadDocumentation}
          disabled={docLoading}
          className="flex-shrink-0 ml-2"
        >
          <RefreshCw className={`h-4 w-4 ${docLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {docLoading ? (
            <DocumentationSkeleton />
          ) : docError ? (
            <DocumentationError message={docError} onRetry={loadDocumentation} />
          ) : docContent ? (
            <MarkdownRenderer content={docContent} />
          ) : (
            <DocumentationPlaceholder />
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="p-3 border-t bg-muted/30">
        <p className="text-xs text-muted-foreground text-center">
          Current page: <code className="px-1 py-0.5 bg-muted rounded">{currentRoute}</code>
        </p>
      </div>
    </div>
  );
}

function DocumentationSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
      <div className="pt-4">
        <Skeleton className="h-6 w-1/2" />
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </div>
      <div className="pt-4">
        <Skeleton className="h-6 w-2/5" />
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    </div>
  );
}

function DocumentationError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <AlertCircle className="h-6 w-6 text-destructive" />
      </div>
      <h3 className="font-semibold mb-2">Unable to Load Documentation</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-xs">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Try Again
      </Button>
    </div>
  );
}

function DocumentationPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <ExternalLink className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="font-semibold mb-2">Documentation</h3>
      <p className="text-sm text-muted-foreground max-w-xs">
        Documentation for this page is being prepared. Check back soon or use the
        chat to ask questions.
      </p>
    </div>
  );
}
